/* Vacancy text detection, cleaning, scoring, and parsing utilities */
import { norm } from './textNormalization';

/**
 * Remove ElevenLabs/agent markers and HTML tags from bot messages.
 * @param {string} text - Raw bot message text
 * @returns {string} Cleaned text
 */
export const cleanBotMessage = (text) => {
  if (!text) return text;

  let cleaned = text
    .replace(/<marker_\d+>/gi, '')
    .replace(/<end_of_subject_identification>/gi, '')
    .replace(/<end_of_core_info>/gi, '')
    .replace(/<end_of_details>/gi, '')
    .replace(/<vacancy_complete>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned;
};

/**
 * Scan conversation messages for the best vacancy text.
 * Scores each bot message on structure keywords, length, formatting,
 * and intro phrases, then returns the highest-quality match.
 * @param {Array} messages - Chat message array ({ who, type, text })
 * @returns {string} Best vacancy text found, or last bot message text
 */
export const detectActualVacancyText = (messages) => {
  const botMessages = messages.filter(m =>
    m.who === 'bot' &&
    m.type !== 'typing' &&
    m.text &&
    m.text.length > 100
  );

  const vacancyMessages = botMessages.filter(message => {
    const text = message.text.toLowerCase();

    const structureKeywords = [
      'functieomschrijving',
      'werkzaamheden',
      'daarom wil je hier werken',
      'functie-eisen',
      'arbeidsvoorwaarden',
      'vereisten',
      'wij bieden'
    ];

    const structureCount = structureKeywords.filter(keyword =>
      text.includes(keyword)
    ).length;

    const hasJobTitle = text.includes('fietsenmaker') ||
                      text.includes('developer') ||
                      text.includes('manager') ||
                      /\b[a-z]+maker\b/.test(text) ||
                      /\b[a-z]+specialist\b/.test(text);

    const hasStructuredContent = text.includes('\u2022') ||
                                text.includes('-') ||
                                text.includes('\u20AC') ||
                                text.split('\n').length > 5;

    const isLongEnough = text.length > 300;

    const hasIntroduction = text.includes('hier is de vacaturetekst') ||
                          text.includes('tijd om die vacaturetekst te bakken') ||
                          text.includes('alle ingredi\u00EBnten binnen') ||
                          text.includes('hier komt ie') ||
                          text.includes('op basis van ons gesprek');

    let score = 0;
    score += structureCount * 20;
    if (hasJobTitle) score += 15;
    if (hasStructuredContent) score += 15;
    if (isLongEnough) score += 10;
    if (hasIntroduction) score += 10;
    if (text.length > 500) score += 10;
    if (text.length > 800) score += 10;

    return score >= 40;
  });

  if (vacancyMessages.length === 0) {
    return botMessages[botMessages.length - 1]?.text || '';
  }

  const bestVacancy = vacancyMessages.sort((a, b) => {
    const scoreA = calculateVacancyQuality(a.text);
    const scoreB = calculateVacancyQuality(b.text);
    return scoreB - scoreA;
  })[0];

  return bestVacancy.text;
};

/**
 * Compute a quality score for vacancy text based on structure,
 * length, and formatting signals.
 * @param {string} text - Vacancy text to score
 * @returns {number} Quality score (higher = better)
 */
export const calculateVacancyQuality = (text) => {
  const lower = text.toLowerCase();

  const structureKeywords = [
    'functieomschrijving', 'werkzaamheden', 'daarom wil je hier werken',
    'functie-eisen', 'arbeidsvoorwaarden', 'vereisten', 'wij bieden'
  ];
  const structureScore = structureKeywords.filter(k => lower.includes(k)).length * 25;

  const lengthScore = Math.min(text.length / 20, 30);

  const formatScore = (text.includes('\u2022') ? 10 : 0) +
                    (text.includes('\u20AC') ? 10 : 0) +
                    (text.split('\n').length > 5 ? 10 : 0);

  return structureScore + lengthScore + formatScore;
};

/**
 * Strip Jesse's conversational intro phrases from vacancy text
 * and normalize to ASCII-safe characters.
 * @param {string} rawText - Raw vacancy text from the bot
 * @returns {string} Cleaned vacancy text ready for display/PDF
 */
export const cleanVacancyText = (rawText) => {
  let cleaned = norm(rawText);

  const introPhrases = [
    /^.*?oké,?\s+ik\s+heb\s+alle\s+ingrediënten\s+binnen\.\s+tijd\s+om\s+die\s+vacaturetekst\s+te\s+bakken.*?hier\s+komt\s+ie\s*:?\s*/i,
    /^.*?hier\s+is\s+de\s+vacaturetekst.*?:\s*/i,
    /^.*?op\s+basis\s+van\s+ons\s+gesprek.*?:\s*/i,
    /^.*?ik\s+heb\s+een\s+vacaturetekst\s+opgesteld.*?:\s*/i,
    /^.*?laat\s+me\s+een\s+vacaturetekst.*?maken.*?:\s*/i,
    /^.*?tijd\s+voor\s+de\s+vacaturetekst.*?:\s*/i,
    /^.*?gebaseerd\s+op\s+jouw\s+input.*?:\s*/i,
    /^.*?nu\s+heb\s+ik\s+alle\s+informatie.*?:\s*/i,
    /^.*?ik\s+zal\s+nu\s+een\s+vacaturetekst\s+opstellen.*?:\s*/i,
    /^.*?goed,\s+op\s+basis\s+van\s+de\s+besproken\s+punten.*?:\s*/i
  ];

  introPhrases.forEach(pattern => {
    cleaned = cleaned.replace(pattern, '');
  });

  cleaned = cleaned.replace(/^.*?(vacature(tekst)?|functie(\s*omschrijving)?)\s*:+\s*/i, '');
  cleaned = cleaned.replace(/^\s*\n+/, '');

  return cleaned.trim();
};

/**
 * Parse vacancy text into named sections by matching Dutch section headers.
 * Returns an object keyed by section name ('intro', 'Werkzaamheden', etc.).
 * @param {string} text - Cleaned vacancy text
 * @returns {Object<string, string>} Section name → content mapping
 */
export const parseVacancyToSections = (text) => {
  const sections = {};
  const lines = text.split('\n').filter(line => line.trim());

  let currentSection = 'intro';
  let currentContent = [];

  const sectionPatterns = [
    { pattern: /^(wat ga je doen|werkzaamheden|taken)/i, name: 'Werkzaamheden' },
    { pattern: /^(waarom zou je|voordelen|waarom)/i, name: 'Waarom deze functie' },
    { pattern: /^(wie ben jij|functie.?eisen|eisen|vereisten)/i, name: 'Functie-eisen' },
    { pattern: /^(wat bieden wij|arbeidsvoorwaarden|aanbod)/i, name: 'Wat bieden wij' },
    { pattern: /^(solliciteren|contact)/i, name: 'Solliciteren' }
  ];

  lines.forEach(line => {
    const trimmed = line.trim();
    const matchedSection = sectionPatterns.find(s => s.pattern.test(trimmed));

    if (matchedSection && currentContent.length > 0) {
      sections[currentSection] = currentContent.join('\n').trim();
      currentSection = matchedSection.name;
      currentContent = [];
    } else if (!matchedSection || currentSection === 'intro') {
      currentContent.push(trimmed);
    }
  });

  if (currentContent.length > 0) {
    sections[currentSection] = currentContent.join('\n').trim();
  }

  return sections;
};
